// src/pages/GuestListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Avatar,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  notification,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  SyncOutlined,
  EnvironmentOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { Option } = Select;

/* ---------- slim design ---------- */
const slimTableStyles = `
  .slim-table .ant-table-thead > tr > th {
    padding: 8px 8px !important;
    font-size: 12px;
    font-weight: 600;
    background-color: #fafafa;
  }
  .slim-table .ant-table-tbody > tr > td {
    padding: 8px 8px !important;
    font-size: 13px;
  }
  .slim-table .ant-btn-sm {
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
    border-radius: 4px;
  }
  .slim-table .ant-tag {
    margin: 0;
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 4px;
  }
  .slim-table .ant-space {
    gap: 4px !important;
  }
  .slim-table .ant-table-pagination {
    margin: 16px 0 !important;
  }
`;

/* ---------- helpers ---------- */
const statusColor = (status) => {
  const map = {
    Confirmed: "gold",
    CheckedIn: "green",
    CheckedOut: "blue",
    Cancelled: "red",
    Canceled: "red",
    Pending: "default",
  };
  return map[status] || "default";
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const H = ({ children }) => <span style={{ whiteSpace: "nowrap" }}>{children}</span>;

const money = (amt, cur = "BWP") => {
  if (amt == null) return "—";
  const n = Number(amt);
  if (!Number.isFinite(n)) return `${cur}${amt}`;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(n);
  } catch {
    return `${cur}${n.toFixed(2)}`;
  }
};

const pick = (o, ...keys) => {
  for (const k of keys) {
    const v = o?.[k];
    if (v != null && String(v).trim() !== "") return v;
  }
  return null;
};

const GuestListPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Inject slim styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = slimTableStyles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  // core data
  const [guests, setGuests] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);

  // ui state
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // drawer state (mode-based)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("details"); // "details" | "history"
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerFull, setDrawerFull] = useState(false);

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [bookings, setBookings] = useState([]);

  // edit modal
  const [isEditGuestModalOpen, setIsEditGuestModalOpen] = useState(false);

  // responsive check
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const breadcrumbItems = [
    { title: <Link to="/dashboard"><HomeOutlined /> Home</Link> },
    { title: "Guests" },
  ];

  const fetchGuests = async (hotelId) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/Guests/hotel/${hotelId}`, {
        params: { page: currentPage, pageSize: itemsPerPage },
      });

      const list = res.data.guests || res.data.Guests || [];
      const total = res.data.totalCount ?? res.data.TotalCount ?? list.length;

      setGuests(list);
      setTotalEntries(total);
      setError(null);
    } catch {
      setError("Failed to load guests.");
      notification.error({ message: "Error", description: "Failed to load guests." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authData?.hotelId) {
      fetchGuests(authData.hotelId);
    } else {
      setError("User authentication is missing. Please log in.");
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.hotelId, currentPage, itemsPerPage]);

  const filteredGuests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return guests;
    return guests.filter((g) =>
      `${g.firstName ?? ""} ${g.lastName ?? ""} ${g.email ?? ""} ${g.idNumber ?? ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [guests, searchQuery]);

  // --- fetch full guest details (for upgraded details view) ---
  const fetchGuestDetails = async (guestId) => {
    if (!guestId) return null;
    try {
      const res = await axiosInstance.get(`/Guests/${guestId}`, {
        params: { hotelId: authData?.hotelId },
      });
      return res.data || null;
    } catch {
      return null;
    }
  };

  const fetchBookingHistory = async (guest) => {
    const idNumber = pick(guest, "idNumber", "IdNumber");
    if (!idNumber) return [];
    const res = await axiosInstance.get(`/Bookings/SearchBookingsByGuest`, {
      params: { idNumber, hotelId: authData.hotelId },
    });
    return res.data || [];
  };

  // --- open drawers (separated actions) ---
  const openGuestDetails = async (guest) => {
    setSelectedGuest(guest);
    setBookings([]);
    setDrawerMode("details");
    setDrawerOpen(true);
    setDrawerFull(false);

    setDrawerLoading(true);
    try {
      const full = await fetchGuestDetails(guest.guestId ?? guest.GuestId);
      if (full) setSelectedGuest((prev) => ({ ...(prev || {}), ...(full || {}) }));
    } finally {
      setDrawerLoading(false);
    }
  };

  const openBookingHistory = async (guest) => {
    setSelectedGuest(guest);
    setBookings([]);
    setDrawerMode("history");
    setDrawerOpen(true);
    setDrawerFull(false);

    setDrawerLoading(true);
    try {
      // parallel: full guest + bookings
      const [full, hist] = await Promise.all([
        fetchGuestDetails(guest.guestId ?? guest.GuestId),
        fetchBookingHistory(guest),
      ]);
      if (full) setSelectedGuest((prev) => ({ ...(prev || {}), ...(full || {}) }));
      setBookings(hist || []);
    } catch (err) {
      notification.info({
        message: "No booking history",
        description:
          err?.response?.status === 404
            ? "No bookings found for this guest."
            : "Could not load booking history.",
      });
      setBookings([]);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleGuestDeleted = async (guest) => {
    try {
      await axiosInstance.delete(`/Guests/${guest.guestId}`);
      notification.success({ message: "Guest deleted" });
      fetchGuests(authData.hotelId);
    } catch {
      notification.error({ message: "Failed to delete guest" });
    }
  };

  /* ---------- table columns ---------- */

  const guestColumns = [
    {
      title: "Guest",
      dataIndex: "firstName",
      width: 220,
      render: (_, r) => (
        <Space size={4}>
          <Tag color="blue">#{r.guestId}</Tag>
          <div style={{ lineHeight: 1.1 }}>
            <Text strong style={{ fontSize: "13px" }}>
              {`${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || "—"}
            </Text>
            <div style={{ fontSize: 11, color: "#888" }}>
              <IdcardOutlined /> {pick(r, "idNumber", "IdNumber") || "N/A"}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 190,
      render: (t) => (
        <Space size={4}>
          <MailOutlined />
          <span style={{ fontSize: "13px" }}>{t || "N/A"}</span>
        </Space>
      ),
      responsive: ["md"],
    },
    {
      title: "Phone",
      dataIndex: "mobileNumber",
      width: 150,
      render: (_, r) => {
        const phone = pick(r, "mobileNumber", "phoneNumber", "MobileNumber", "PhoneNumber");
        return (
          <Space size={4}>
            <PhoneOutlined />
            <span style={{ fontSize: "13px" }}>{phone || "N/A"}</span>
          </Space>
        );
      },
      responsive: ["lg"],
    },
    {
      title: "Address",
      dataIndex: "address",
      width: 180,
      render: (_, r) => {
        const addr =
          pick(r, "postalAddress", "PostalAddress") ||
          pick(r, "residentialAddress", "ResidentialAddress") ||
          pick(r, "address", "Address");
        return (
          <Space size={4}>
            <EnvironmentOutlined />
            <span style={{ fontSize: "13px" }}>{addr || "N/A"}</span>
          </Space>
        );
      },
      responsive: ["xl"],
    },
    {
      title: "Registered",
      dataIndex: "arrivalDate",
      width: 120,
      render: (_, r) => fmtDate(pick(r, "arrivalDate", "ArrivalDate", "createdAt", "CreatedAt")),
      sorter: (a, b) =>
        new Date(pick(a, "arrivalDate", "ArrivalDate", "createdAt", "CreatedAt") || 0).getTime() -
        new Date(pick(b, "arrivalDate", "ArrivalDate", "createdAt", "CreatedAt") || 0).getTime(),
    },
    {
      title: "Actions",
      fixed: "right",
      width: 220,
      render: (_, record) => (
        <Space size={6} wrap>
          <Tooltip title="View guest details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openGuestDetails(record)}
            >
              Details
            </Button>
          </Tooltip>

          <Tooltip title="View booking history">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => openBookingHistory(record)}
            >
              History
            </Button>
          </Tooltip>

          <Tooltip title="Edit guest">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => setIsEditGuestModalOpen(true)}
            />
          </Tooltip>

          <Tooltip title="Delete guest">
            <Popconfirm
              title="Delete this guest?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleGuestDeleted(record)}
            >
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const bookingColumns = [
    {
      title: <H>Booking #</H>,
      dataIndex: "bookingNo",
      width: 120,
      ellipsis: true,
      render: (t, r) => {
        const id = r.bookingId ?? t;
        const label = t || id || "—";
        return id ? (
          <a href={`/bookings/${id}`} onClick={(e) => e.stopPropagation()}>
            {label}
          </a>
        ) : (
          label
        );
      },
    },
    { title: <H>Room</H>, width: 80, render: (_, r) => r.room?.roomNumber || r.roomNumber || "—" },
    { title: <H>Check-In</H>, dataIndex: "checkInDate", width: 110, render: fmtDate },
    { title: <H>Check-Out</H>, dataIndex: "checkOutDate", width: 110, render: fmtDate },
    {
      title: <H>Nights</H>,
      width: 70,
      align: "right",
      render: (_, r) => {
        const a = r.checkInDate ? new Date(r.checkInDate) : null;
        const b = r.checkOutDate ? new Date(r.checkOutDate) : null;
        if (!a || !b) return "—";
        return Math.max(Math.ceil((b - a) / 86400000), 1);
      },
    },
    {
      title: <H>Status</H>,
      dataIndex: "status",
      width: 100,
      render: (s) => <Tag color={statusColor(s)}>{s || "—"}</Tag>,
    },
    {
      title: <H>Total</H>,
      align: "right",
      width: 110,
      render: (_, r) => money(r.totalAmount ?? r.TotalAmount, r.currency ?? r.Currency ?? "BWP"),
    },
    {
      title: <H>Paid</H>,
      align: "right",
      width: 110,
      render: (_, r) => money(r.paidAmount ?? r.PaidAmount ?? 0, r.currency ?? r.Currency ?? "BWP"),
    },
    {
      title: <H>Balance</H>,
      align: "right",
      width: 110,
      render: (_, r) => {
        const total = Number(r.totalAmount ?? r.TotalAmount ?? 0);
        const paid = Number(r.paidAmount ?? r.PaidAmount ?? 0);
        const cur = r.currency ?? r.Currency ?? "BWP";
        const bal = Number.isFinite(total - paid) ? Math.max(total - paid, 0) : 0;
        return <Text strong style={{ fontSize: "13px" }}>{money(bal, cur)}</Text>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Spin size="large" tip="Loading guests..." />
      </div>
    );
  }

  const drawerWidth = drawerFull || isMobile ? "100%" : 860;

  const g = selectedGuest || {};
  const fullName = `${pick(g, "firstName", "FirstName") ?? ""} ${pick(g, "lastName", "LastName") ?? ""}`.trim();
  const idNumber = pick(g, "idNumber", "IdNumber");
  const dob = pick(g, "dateOfBirth", "DateOfBirth", "dob");
  const email = pick(g, "email", "Email");
  const mobile = pick(g, "mobileNumber", "MobileNumber");
  const phone = pick(g, "phoneNumber", "PhoneNumber");
  const companyName = pick(g, "companyName", "CompanyName", "company", "Company");
  const companyEmail = pick(g, "companyEmail", "CompanyEmail");
  const postalAddress = pick(g, "postalAddress", "PostalAddress");
  const residentialAddress = pick(g, "residentialAddress", "ResidentialAddress");
  const legacyAddress = pick(g, "address", "Address");
  const notes = pick(g, "notes", "Notes");
  const remarks = pick(g, "remarks", "Remarks");

  return (
    <div className="guest-history-page">
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        title={
          <Space>
            <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
            <Text strong>Guests</Text>
            <Badge count={guests.length} showZero style={{ backgroundColor: "#1890ff" }} />
            <Button
              type="text"
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={() =>
                Modal.info({
                  title: "Guests",
                  content: (
                    <div>
                      <p>Browse all registered guests.</p>
                      <p>
                        Use <b>Details</b> for guest profile and <b>History</b> for booking history.
                      </p>
                    </div>
                  ),
                })
              }
            />
          </Space>
        }
        extra={
          <Space>
            <Button
              size="small"
              icon={<SyncOutlined />}
              onClick={() => fetchGuests(authData.hotelId)}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
        variant="outlined"
        bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}
      >
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Tag color="red">{error}</Tag>
            <Button size="small" type="link" onClick={() => fetchGuests(authData.hotelId)}>
              Retry
            </Button>
          </div>
        )}

        {/* Filters */}
        <Card variant="outlined" bodyStyle={{ paddingBottom: 8 }}>
          <Space wrap>
            <Input
              style={{ width: 280 }}
              placeholder="Search by name, email, ID…"
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
            <Select
              style={{ width: 140 }}
              value={itemsPerPage}
              onChange={(v) => {
                setItemsPerPage(v);
                setCurrentPage(1);
              }}
            >
              <Option value={5}>5 / page</Option>
              <Option value={10}>10 / page</Option>
              <Option value={25}>25 / page</Option>
              <Option value={50}>50 / page</Option>
            </Select>
            <Button type="primary" onClick={() => setCurrentPage(1)}>
              Apply
            </Button>
            <Button
              onClick={() => {
                setSearchQuery("");
                setCurrentPage(1);
              }}
            >
              Reset
            </Button>
          </Space>
        </Card>

        {/* Table */}
        <Card style={{ marginTop: 16 }}>
          {filteredGuests.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text>No guests found</Text>} />
          ) : (
            <Table
              className="slim-table"
              columns={guestColumns}
              dataSource={filteredGuests}
              rowKey="guestId"
              tableLayout="fixed"
              scroll={{ x: 1200 }}
              pagination={{
                current: currentPage,
                pageSize: itemsPerPage,
                total: totalEntries,
                onChange: (p) => setCurrentPage(p),
                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} guests`,
                showSizeChanger: false,
              }}
            />
          )}
        </Card>
      </Card>

      {/* Drawer: Mode-based (Details OR History) */}
      <Drawer
        title={
          <Space>
            <Avatar icon={<UserOutlined />} />
            <span>{drawerMode === "history" ? "Booking History" : "Guest Details"}</span>
            {selectedGuest?.guestId ? <Tag color="blue">#{selectedGuest.guestId}</Tag> : null}
          </Space>
        }
        placement="right"
        width={drawerWidth}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerFull(false);
          setDrawerLoading(false);
          setBookings([]);
        }}
        open={drawerOpen}
        destroyOnClose
        extra={
          <Space>
            {/* Quick switch (optional, still keeps separate actions in table) */}
            {drawerMode === "details" ? (
              <Tooltip title="Go to booking history">
                <Button size="small" icon={<FileTextOutlined />} onClick={() => openBookingHistory(selectedGuest)}>
                  History
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Go to guest details">
                <Button size="small" icon={<EyeOutlined />} onClick={() => openGuestDetails(selectedGuest)}>
                  Details
                </Button>
              </Tooltip>
            )}

            <Tooltip title={drawerFull ? "Exit full screen" : "Full screen"}>
              <Button
                type="text"
                size="small"
                icon={drawerFull ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setDrawerFull((v) => !v)}
              />
            </Tooltip>
          </Space>
        }
        styles={{
          header: { padding: "10px 16px" },
          body: { paddingTop: 12 },
          content: { maxWidth: "100vw" },
        }}
      >
        {drawerLoading ? (
          <Spin tip={drawerMode === "history" ? "Loading history..." : "Loading guest..."} />
        ) : selectedGuest ? (
          drawerMode === "history" ? (
            <>
              <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Name" span={2}>
                  <Text strong>{fullName || "—"}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ID/Passport">{idNumber || "—"}</Descriptions.Item>
                <Descriptions.Item label="Email">
                  <Space size={4}>
                    <MailOutlined /> {email || "—"}
                  </Space>
                </Descriptions.Item>
              </Descriptions>

              {bookings?.length ? (
                <Table
                  className="slim-table"
                  rowKey={(r) => r.bookingId ?? r.bookingNo ?? `${r.checkInDate}-${r.room?.roomNumber}`}
                  columns={bookingColumns}
                  dataSource={bookings}
                  size="small"
                  bordered
                  tableLayout="fixed"
                  scroll={{ x: true, y: drawerFull || isMobile ? "calc(100vh - 330px)" : 360 }}
                  pagination={{ pageSize: 8, showSizeChanger: true }}
                />
              ) : (
                <Empty description="No bookings found for this guest" />
              )}
            </>
          ) : (
            <>
              <Descriptions bordered size="small" column={isMobile ? 1 : 2} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Name" span={isMobile ? 1 : 2}>
                  <Text strong>{fullName || "—"}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Guest ID">
                  {pick(g, "guestId", "GuestId") ?? "—"}
                </Descriptions.Item>

                <Descriptions.Item label="ID/Passport">
                  {idNumber || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Date of Birth">
                  {dob ? fmtDate(dob) : "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Email">
                  <Space size={4}>
                    <MailOutlined /> {email || "—"}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Mobile">
                  <Space size={4}>
                    <PhoneOutlined /> {mobile || "—"}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Phone">
                  <Space size={4}>
                    <PhoneOutlined /> {phone || "—"}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Company">
                  {companyName || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Company Email">
                  {companyEmail || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Postal Address" span={isMobile ? 1 : 2}>
                  {postalAddress || legacyAddress || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Residential Address" span={isMobile ? 1 : 2}>
                  {residentialAddress || legacyAddress || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Remarks" span={isMobile ? 1 : 2}>
                  {remarks || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Notes" span={isMobile ? 1 : 2}>
                  {notes || "—"}
                </Descriptions.Item>
              </Descriptions>

              <Space>
                <Button
                  size="small"
                  icon={<FileTextOutlined />}
                  onClick={() => openBookingHistory(selectedGuest)}
                >
                  View Booking History
                </Button>
              </Space>
            </>
          )
        ) : null}
      </Drawer>

      {/* Edit Guest (still placeholder) */}
      <Modal
        open={isEditGuestModalOpen}
        title={
          <Space>
            <EditOutlined />
            <Text strong>Edit Guest</Text>
          </Space>
        }
        onCancel={() => setIsEditGuestModalOpen(false)}
        footer={null}
        width={540}
      >
        <Form
          layout="vertical"
          onFinish={() => {
            notification.success({ message: "Guest updated" });
            setIsEditGuestModalOpen(false);
            fetchGuests(authData.hotelId);
          }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ type: "email" }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mobileNumber" label="Mobile">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="postalAddress" label="Postal Address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="residentialAddress" label="Residential Address">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Space>
            <Button onClick={() => setIsEditGuestModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default GuestListPage;
